import React, { useState, useEffect } from "react";
import { message, Button, Spin, Input, Tag } from "antd";
import dayjs from "dayjs";
import { putRequest, getRequest, postRequest } from "../service/index";
import { getEmployeeId, getEmployeeData } from "../utils/localStorage";

const ApprovedOverTime = ({ onClose, onSuccess, initialData = {} }) => {
  
  const [loading, setLoading] = useState(false);
  const employeeId = getEmployeeId();
  const employedate = getEmployeeData();
  const [employeeList, setEmployeeList] = useState();
  const [overtimeDetails, setOvertimeDetails] = useState({
    employeeName: "",
    date: "",
    checkIn: "",
    checkOut: "",
    workingHour: "",
    totalOverTime: "",
    paidOverTime: "",
    status: "",
    approvedBy: "",
    employeeId: "",
    details: "",
    id: ""
  });

  // Update overtimeDetails when initialData changes
  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      setOvertimeDetails({
        employeeName: initialData.employeeName || "",
        date: initialData.date || "",
        checkIn: initialData.checkIn || "",
        checkOut: initialData.checkOut || "",
        workingHour: initialData.workingHour || "",
        totalOverTime: initialData.totalOverTime || "",
        paidOverTime: initialData.paidOverTime || "",
        status: initialData.status || "",
        approvedBy: initialData.approvedBy || "",
        employeeId: initialData.employeeId || "",
        details: initialData.originalData.details || "",
        id: initialData.id || ""
      });
    }
    const fetchEmployeeList = async () => {
      try {
        if (initialData?.employeeId) {
          const response = await getRequest(`/api/companies/teammembers?employeeId=${initialData.employeeId}`);
          if (response?.employee) {
            setEmployeeList(response);
          }
        }
      } catch (error) {
        console.error('Error fetching employee data:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchEmployeeList();
  }, [initialData]);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleApprove = async () => {
    try {
      setLoading(true);
      
      const payload = {
        status: "approved",
        approvedby: employeeId,
        totalOverTime: overtimeDetails.totalOverTime,
        paidOverTime: overtimeDetails.paidOverTime
      };

      const response = await putRequest(`/api/overtimerequest?id=${overtimeDetails.id}`, payload);
      const managementIds = (employeeList?.managementChain || []).map(m => m?._id).filter(Boolean);
      const uniqueReceiverIds = Array.from(new Set([initialData?.employeeId, ...managementIds].filter(Boolean)));
      const res = await postRequest('/api/notifications', {
        "title": `${employeeList.employee.name} Request OverTime Approved`,
        "senderId": employeeId,
        "receiverIds": uniqueReceiverIds,
        "type": "OverTimeRequest",
        "relatedTypeId": response.overtimeRequest._id,
        "relatedDocumentModel": "OverTimeRequest",
        "companyId": employedate?.companyId,
        "priority": "medium"
      });
      if (response.status === 200 || response.message === "OvertimeRequest updated successfully") {
        message.success(response.message || "Overtime request approved successfully");
        
        // Make additional API call to update attendance
        if (initialData.originalData?.attendanceLog?._id) {
          try {
            const attendancePayload = {
              approvedbyId: employeeId,
              overtimeSalery: overtimeDetails.paidOverTime,
              overtime: overtimeDetails.totalOverTime
            };

            const attendanceResponse = await putRequest(`/api/myattendance/${initialData.originalData.attendanceLog._id}`, attendancePayload);
            
            if ( attendanceResponse.message === "Attendance log updated successfully") {
              message.success("Attendance record updated successfully");
            } else {
              message.warning("Overtime approved but attendance update failed");
            }
          } catch (attendanceError) {
            console.error("Error updating attendance:", attendanceError);
            message.warning("Overtime approved but attendance update failed");
          }
        }
        
        onSuccess && onSuccess();
        onClose();
      } else {
        message.error(response.error || "Failed to approve overtime request");
      }
    } catch (error) {
      console.error("Error approving overtime:", error);
      message.error("Failed to approve overtime. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    try {
      setLoading(true);
      
      const payload = {
        status: "rejected",
        approvedby: employeeId,
        totalOverTime: overtimeDetails.totalOverTime,
        paidOverTime: overtimeDetails.paidOverTime
      };

      const response = await putRequest(`/api/overtimerequest?id=${overtimeDetails.id}`, payload);
      const managementIds = (employeeList?.managementChain || []).map(m => m?._id).filter(Boolean);
      const uniqueReceiverIds = Array.from(new Set([initialData?.employeeId, ...managementIds].filter(Boolean)));
      const res = await postRequest('/api/notifications', {
        "title": `${employeeList.employee.name} Request OverTime Rejected`,
        "senderId": employeeId,
        "receiverIds": uniqueReceiverIds,
        "type": "OverTimeRequest",
        "relatedTypeId": response.overtimeRequest._id,
        "relatedDocumentModel": "OverTimeRequest",
        "companyId": employedate?.companyId,
        "priority": "high"
      });
      if (response.status === 200 || response.message === "OvertimeRequest updated successfully") {
        message.success(response.message || "Overtime request rejected successfully");
        onSuccess && onSuccess();
        onClose();
      } else {
        message.error(response.message || "Failed to reject overtime request");
      }
    } catch (error) {
      console.error("Error rejecting overtime:", error);
      message.error("Failed to reject overtime. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Spin spinning={loading}>
      <div className="flex flex-col gap-4 max-h-[80vh] overflow-y-auto pr-2">
        {/* Employee Details Display */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-600 mb-1">Employee Name</label>
              <div className="text-md font-semibold text-gray-800">{overtimeDetails.employeeName || 'N/A'}</div>
            </div>
            
            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-600 mb-1">Date</label>
              <div className="text-md font-semibold text-gray-800">{overtimeDetails.date || 'N/A'}</div>
            </div>
            
            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-600 mb-1">Check In</label>
              <div className="text-md font-semibold text-gray-800">{overtimeDetails.checkIn || 'N/A'}</div>
            </div>
            
            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-600 mb-1">Check Out</label>
              <div className="text-md font-semibold text-gray-800">{overtimeDetails.checkOut || 'N/A'}</div>
            </div>
            
            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-600 mb-1">Working Hours</label>
              <div className="text-md font-semibold text-gray-800">{overtimeDetails.workingHour || 'N/A'}</div>
            </div>
            
            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-600 mb-1">Total Overtime</label>
              <div className="text-md font-semibold text-blue-600">{overtimeDetails.totalOverTime || 'N/A'}</div>
            </div>
            
            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-600 mb-1">Paid Overtime</label>
              <div className="text-md font-semibold text-green-600">{overtimeDetails.paidOverTime || 'N/A'}</div>
            </div>
            
            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-600 mb-1">Status</label>
              <div className="text-md font-semibold">
                <Tag color={getStatusColor(overtimeDetails.status)}>
                  {overtimeDetails.status || 'N/A'}
                </Tag>
              </div>
            </div>
          </div>
          <div className="flex flex-col mt-4">
            <label className="text-sm font-medium text-gray-600 mb-1">Details</label>
            <div className="text-md text-gray-800">{overtimeDetails.details || 'N/A'}</div>
          </div>
        </div>

        {/* Editable Fields for Approval/Rejection */}
        <div className="bg-blue-50 p-4 rounded-lg">
          <h4 className="text-md font-semibold text-gray-800 mb-3">Update Overtime Details</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-600 mb-1">Total Overtime</label>
              <Input
                value={overtimeDetails.totalOverTime}
                onChange={(e) => setOvertimeDetails({...overtimeDetails, totalOverTime: e.target.value})}
                placeholder="Enter total overtime (e.g., 01:30)"
                className="text-blue-600 font-semibold"
              />
            </div>
            
            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-600 mb-1">Paid Overtime</label>
              <Input
                value={overtimeDetails.paidOverTime}
                onChange={(e) => setOvertimeDetails({...overtimeDetails, paidOverTime: e.target.value})}
                placeholder="Enter paid overtime amount (e.g., 45.00)"
                className="text-green-600 font-semibold"
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3 my-4">
        <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleReject}
            loading={loading}
            danger
            type="primary"
            className="bg-primary text-white"
            disabled={loading}
          >
            {loading ? "Processing..." : "Reject"}
          </Button>
          <Button
            onClick={handleApprove}
            loading={loading}
            type="primary"
            className="bg-primary text-white "
            disabled={loading}
          >
            {loading ? "Processing..." : "Approve"}
          </Button>
        
        </div>
      </div>
    </Spin>
  );
};

export default ApprovedOverTime;
